<script setup>
import { computed, watch, getCurrentInstance } from 'vue'
import { useAsyncState } from '@vueuse/core'
import FormControlPlainText from "../../common/form-control/FormControlPlainText.vue"

const props = defineProps({
    currentData: Object,
    field: Object,
    formId: String
})

const { proxy } = getCurrentInstance()
const store = proxy.$store

// --- State Helpers ---
const tableName = computed(() => store.state[props.formId].tableName)
const id = computed(() => store.state[props.formId].id)
const courseId = computed(() => props.currentData.courseId)

/**
 * Custom Commit Helper
 * Presets data in the specific form module so changes aren't flagged as dirty immediately.
 */
const presetFormValue = (value) => {
    store.commit(`${props.formId}/presetData`, {
        name: props.field.name,
        value: value
    })
}

// --- Initialization (Formerly 'created') ---
if (!id.value) {
    presetFormValue([])
}

/**
 * Fetch Promotions based on Franchise ID
 * Replaces the first asyncComputed
 */
const { state: promotions, execute: fetchPromotions } = useAsyncState(async () => {
    if (!courseId.value) return []

    const course = await store.dispatch('itemData/get', {
        tableName: 'odp_courses',
        id: courseId.value
    })

    const ids = await store.dispatch('itemIds/get', {
        tableName: 'odp_promotions'
    })

    const allPromotions = await store.dispatch('itemData/preload', {
        tableName: 'odp_promotions',
        ids
    })

    return allPromotions.filter(o => o.franchiseId === course.franchiseId)
}, [])

/**
 * Fetch Current Selected Category IDs
 * Replaces the second asyncComputed
 */
const { state: categoryIds } = useAsyncState(async () => {
    const data = await store.dispatch('categories/getTableColumnData', {
        tableName: tableName.value,
        columnName: props.field.name
    })

    const filteredIds = data
        .filter(o => o.itemId === id.value)
        ?.map(o => o.catId) || []

    // Sync back to form data using your custom commit
    presetFormValue(filteredIds)

    return filteredIds
}, [])

// --- Watchers ---
watch(courseId, () => fetchPromotions())

// --- Methods ---
const toggleSelection = (promotionId) => {
    // Access the array from currentData (populated via the presetData commit)
    const currentList = props.currentData[props.field.name]
    if (!Array.isArray(currentList)) return

    const i = currentList.indexOf(promotionId)
    if (i === -1) {
        currentList.push(promotionId)
    } else {
        currentList.splice(i, 1)
    }
}
</script>

<template>
    <div>
        <FormControlPlainText v-if="promotions.length === 0" :form-id="formId">
            <em>No promotions available.</em>
        </FormControlPlainText>

        <div v-for="promotion in promotions" :key="promotion.id" class="radio">
            <label>
                <input
                    :checked="categoryIds?.indexOf(promotion.id) > -1"
                    type="checkbox"
                    @click="toggleSelection(promotion.id)"
                >
                {{ promotion.title }}
            </label>
        </div>
    </div>
</template>